'use client';

import { useCallback, useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import Image from 'next/image';
import { useWorkspaceActions } from './store';
import { useFullscreenNav } from './fullscreen-nav';
import { useWorkspaceCardActions } from './card-actions-context';
import { openWorkspacePanel, requestPostOpen } from './panels/panel-events';
import { Avatar } from '@/components/avatar';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/components/auth-provider';
import { AlertCircle, Plus, FileText, Image as ImageIcon, Bookmark, Bell, BellPlus, MessageSquare } from 'lucide-react';

/**
 * Preview dialogs shown when an authed user lands on a shareable
 * deep-link route (`/u/<name>`, `/p/<id>`, `/content/<id>`). The
 * shareable redirect funnels them to `/workspace/<id>?preview=<kind>&...`,
 * the `WorkspaceDeepLinkConsumer` opens
 * `/preview/<kind>?...` against the FullscreenNav stack, and the
 * registry maps that pathname to one of the components in this file.
 *
 * Each preview renders a compact read-only card (avatar/title +
 * a few stats) — NOT the full standalone view. The whole point of
 * the preview is to give the user an opt-in commit: click
 * "Add to workspace" to spawn the corresponding canvas node
 * (AgentNode for profiles, PostNode for posts, ContentNode for
 * content), or close to dismiss without polluting the canvas.
 */

// ---------------------------------------------------------------------------
// Profile preview (/preview/profile?username=foo)
// ---------------------------------------------------------------------------

type ProfileSummary = {
  id: string;
  username: string;
  displayName: string;
  avatarUrl?: string | null;
  bio?: string | null;
  accountType: 'human' | 'agent';
  followerCount?: number;
  postCount?: number;
  /** Viewer-scoped; `null` when nobody is signed in. */
  isFollowing?: boolean | null;
};

export function ProfilePreview({ params }: { params: URLSearchParams }) {
  const t = useT();
  const handle = (params.get('username') ?? '').replace(/^@+/, '');
  const [profile, setProfile] = useState<ProfileSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!handle) {
      setError('No username provided');
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/users/${encodeURIComponent(handle)}`);
        if (!res.ok) {
          if (!cancelled) {
            setError(res.status === 404 ? t('workspace.preview.userNotFound') : t('workspace.preview.profileLoadFailed'));
          }
          return;
        }
        const data = (await res.json()) as { user?: ProfileSummary };
        if (!cancelled) setProfile(data.user ?? null);
      } catch {
        if (!cancelled) setError(t('workspace.preview.profileLoadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handle]);

  if (loading) return <PreviewSpinner />;
  if (error || !profile) return <PreviewError message={error ?? t('workspace.preview.userNotFound')} />;

  const isAgent = profile.accountType === 'agent';
  return (
    <div className="flex flex-col items-center gap-4 py-6 text-center">
      <Avatar
        seed={profile.username}
        type={isAgent ? 'agent' : 'human'}
        src={profile.avatarUrl ?? undefined}
        className="h-24 w-24 ring-2 ring-border"
      />
      <div className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight">{profile.displayName}</h2>
        <p
          className={isAgent ? 'text-sm text-purple-500' : 'text-sm text-emerald-500'}
        >
          @{profile.username}
        </p>
        <span
          className={
            isAgent
              ? 'inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-purple-500/15 text-purple-500'
              : 'inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-emerald-500/15 text-emerald-500'
          }
        >
          {isAgent ? t('user.agent') : t('user.human')}
        </span>
      </div>
      {profile.bio && (
        <p className="max-w-sm text-sm text-muted-foreground line-clamp-4">
          {profile.bio}
        </p>
      )}
      {(profile.followerCount != null || profile.postCount != null) && (
        <div className="flex gap-6 text-xs text-muted-foreground">
          {profile.postCount != null && (
            <span>
              <span className="font-medium text-foreground">{profile.postCount}</span> posts
            </span>
          )}
          {profile.followerCount != null && (
            <span>
              <span className="font-medium text-foreground">{profile.followerCount}</span>{' '}
              followers
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Profile footer.
 *
 * The CTA depends on WHO the profile belongs to, because "add to
 * workspace" only ever meant something for agents. An agent on the
 * canvas is a durable surface: its own chats list, its settings, and
 * whatever custom UI its owner published. A person has none of that.
 * The only thing a person's widget can be is one conversation, so
 * offering "Add to workspace" for a human was describing the mechanic
 * (a canvas node gets spawned) instead of the thing the person wanted,
 * which is to talk to them. Same spawn, honest label: Chat.
 *
 * Alongside it, Follow, which is the other useful commitment on a
 * person and matches `PostPreviewFooter`'s Save + Follow pairing.
 *
 * On your OWN profile there is no action at all. There is nothing to
 * follow and nobody to message, and the New-chat picker has always
 * filtered you out of its results for the same reason.
 */
export function ProfilePreviewFooter({ params }: { params: URLSearchParams }) {
  const t = useT();
  const handle = (params.get('username') ?? '').replace(/^@+/, '');
  const wsCardActions = useWorkspaceCardActions();
  const { addItem, findFirstByRef, bringToFront } = useWorkspaceActions();
  const { close } = useFullscreenNav();
  const { user: viewer } = useAuth();
  const [profile, setProfile] = useState<ProfileSummary | null>(null);
  const [following, setFollowing] = useState(false);
  const [working, setWorking] = useState<'spawn' | 'follow' | null>(null);

  // One read for both buttons: the spawn needs the user id, the Follow
  // button needs its own starting state (seeded, not assumed — see the
  // same note on `PostPreviewFooter`, where assuming "not following"
  // silently unfollowed people who already were).
  useEffect(() => {
    if (!handle) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/users/${encodeURIComponent(handle)}`);
        if (!res.ok) return;
        const data = (await res.json()) as { user?: ProfileSummary };
        if (cancelled || !data.user) return;
        setProfile(data.user);
        setFollowing(!!data.user.isFollowing);
      } catch {
        /* leave the footer in its loading shape */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handle]);

  const isSelf = !!viewer && !!profile && viewer.id === profile.id;
  const isAgent = profile?.accountType === 'agent';

  const onSpawn = useCallback(async () => {
    if (working || !profile?.id) return;
    // There is one conversation with a person, not one per click, so a
    // second Chat focuses the window that is already open rather than
    // stacking a duplicate on the canvas. Same dedup the `@mention`
    // spawn path in `AgentNode` does.
    const existing = findFirstByRef(profile.id);
    if (existing) {
      bringToFront(existing.id);
      close();
      return;
    }
    setWorking('spawn');
    try {
      if (wsCardActions) {
        await wsCardActions.addAgent({
          agentId: profile.id,
          username: profile.username,
          displayName: profile.displayName ?? profile.username,
          avatarUrl: profile.avatarUrl ?? null,
          accountType: profile.accountType,
        });
      } else {
        await addItem({
          type: 'agent',
          refId: profile.id,
          state: {
            cache: {
              username: profile.username,
              displayName: profile.displayName ?? profile.username,
              avatarUrl: profile.avatarUrl ?? null,
              accountType: profile.accountType,
            },
          },
        });
      }
      close();
    } finally {
      setWorking(null);
    }
  }, [profile, wsCardActions, addItem, close, working, findFirstByRef, bringToFront]);

  const onFollow = useCallback(async () => {
    if (working || !profile?.username) return;
    setWorking('follow');
    try {
      const res = await fetch(`/api/users/${encodeURIComponent(profile.username)}/follow`, {
        method: 'POST',
      });
      if (!res.ok) return;
      const data = (await res.json()) as { following?: boolean };
      const next = !!data.following;
      setFollowing(next);
      window.dispatchEvent(
        new CustomEvent('followChanged', {
          detail: {
            type: profile.accountType === 'agent' ? 'agent' : 'human',
            username: profile.username,
            following: next,
          },
        }),
      );
    } finally {
      setWorking(null);
    }
  }, [profile, working]);

  // Agents keep the canvas CTA: for them the widget really is more than
  // a chat window, so "Add to workspace" says what it does.
  if (isAgent) {
    return <AddToWorkspaceFooter onAdd={onSpawn} working={working === 'spawn'} />;
  }

  return (
    <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border bg-background/60">
      <button
        type="button"
        onClick={close}
        className="h-9 px-4 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
      >
        Close
      </button>
      {!isSelf && !!profile && (
        <>
          <button
            type="button"
            onClick={onFollow}
            disabled={working === 'follow'}
            className="h-9 px-4 rounded-lg text-sm font-medium border border-border text-foreground hover:bg-accent transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {working === 'follow' ? (
              <Spinner size="xs" tone="muted" />
            ) : following ? (
              <Bell className="h-3.5 w-3.5 fill-current" />
            ) : (
              <BellPlus className="h-3.5 w-3.5" />
            )}
            {following ? t('workspace.preview.following') : t('workspace.preview.follow')}
          </button>
          <button
            type="button"
            onClick={onSpawn}
            disabled={working === 'spawn'}
            className="h-9 px-4 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:opacity-90 transition-opacity inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {working === 'spawn' ? (
              <Spinner size="xs" />
            ) : (
              <MessageSquare className="h-3.5 w-3.5" />
            )}
            {t('posts.chat')}
          </button>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Post preview (/preview/post?postId=xxx)
// ---------------------------------------------------------------------------

type PostSummary = {
  id: string;
  title?: string | null;
  content?: string | null;
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
  mediaType?: string | null;
  author: {
    id: string;
    username: string;
    displayName?: string | null;
    avatarUrl?: string | null;
    accountType?: string;
  };
  channel?: { slug: string; name: string } | null;
  totalLikes?: number;
  commentCount?: number;
};

export function PostPreview({ params }: { params: URLSearchParams }) {
  const t = useT();
  const postId = params.get('postId') ?? '';
  const [post, setPost] = useState<PostSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!postId) {
      setError('No post id');
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/posts/${encodeURIComponent(postId)}`);
        if (!res.ok) {
          if (!cancelled) {
            setError(res.status === 404 ? t('workspace.preview.postNotFound') : t('workspace.preview.postLoadFailed'));
          }
          return;
        }
        const data = (await res.json()) as { post?: PostSummary };
        if (!cancelled) setPost(data.post ?? null);
      } catch {
        if (!cancelled) setError(t('workspace.preview.postLoadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [postId]);

  if (loading) return <PreviewSpinner />;
  if (error || !post) return <PreviewError message={error ?? t('workspace.preview.postNotFound')} />;

  const isAgent = post.author.accountType === 'agent';
  return (
    <article className="space-y-4 py-2">
      <header className="flex items-center gap-3">
        <Avatar
          seed={post.author.username}
          type={isAgent ? 'agent' : 'human'}
          src={post.author.avatarUrl ?? undefined}
          className="h-10 w-10 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">
            {post.author.displayName ?? post.author.username}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            @{post.author.username}
            {post.channel && <span> &middot; //{post.channel.slug}</span>}
          </p>
        </div>
      </header>
      {post.title && <h2 className="text-lg font-semibold">{post.title}</h2>}
      {(post.thumbnailUrl || post.mediaUrl) && post.mediaType !== 'audio' && (
        <div className="overflow-hidden rounded-lg bg-muted">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={post.thumbnailUrl ?? post.mediaUrl ?? ''}
            alt={post.title ?? 'Post media'}
            className="w-full max-h-80 object-contain"
          />
        </div>
      )}
      {post.content && (
        <p className="text-sm whitespace-pre-wrap line-clamp-[10]">{post.content}</p>
      )}
      {(post.totalLikes != null || post.commentCount != null) && (
        <div className="flex gap-4 text-xs text-muted-foreground border-t border-border pt-3">
          {post.totalLikes != null && (
            <span>
              <span className="font-medium text-foreground">{post.totalLikes}</span> likes
            </span>
          )}
          {post.commentCount != null && (
            <span>
              <span className="font-medium text-foreground">{post.commentCount}</span>{' '}
              comments
            </span>
          )}
        </div>
      )}
    </article>
  );
}

/**
 * Post footer — unlike profiles/content, a post is not "added to the
 * workspace canvas" (that CTA is reserved for apps and agents). The
 * useful commitments on a shared post are Save (bookmark) and Follow
 * (subscribe to its thread), so those are the two actions here. Reading
 * and replying happen in the in-panel detail view, not this preview.
 */
export function PostPreviewFooter({ params }: { params: URLSearchParams }) {
  const t = useT();
  const postId = params.get('postId') ?? '';
  const { close } = useFullscreenNav();
  const [saved, setSaved] = useState(false);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState<'save' | 'follow' | null>(null);

  // Seed from the post rather than assuming "not saved / not following":
  // the buttons used to read Save/Follow for a post the viewer had already
  // saved and followed, so clicking them silently undid both.
  useEffect(() => {
    if (!postId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/posts/${encodeURIComponent(postId)}`);
        if (!res.ok) return;
        const data = (await res.json()) as {
          post?: { isSaved?: boolean; isFollowing?: boolean };
        };
        if (cancelled || !data.post) return;
        setSaved(!!data.post.isSaved);
        setFollowing(!!data.post.isFollowing);
      } catch {
        /* leave the defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [postId]);

  // The thread lives in the Feed panel's detail view; this dialog is where a
  // shared `/p/<id>` link lands, so without this there was no way through to
  // reading or replying.
  const openThread = useCallback(() => {
    if (!postId) return;
    // `requestPostOpen` parks the target until the panel that owns the detail
    // stack picks it up, and that panel selects the Feed view itself — so this
    // only has to make sure the panel is open and get the dialog out of the way.
    requestPostOpen(postId);
    openWorkspacePanel('left', { tab: 'feed' });
    close();
  }, [postId, close]);

  const toggleSave = useCallback(async () => {
    if (busy || !postId) return;
    setBusy('save');
    try {
      const res = await fetch(`/api/posts/${encodeURIComponent(postId)}/save`, {
        method: 'POST',
      });
      if (res.ok) setSaved((s) => !s);
    } finally {
      setBusy(null);
    }
  }, [postId, busy]);

  const toggleFollow = useCallback(async () => {
    if (busy || !postId) return;
    setBusy('follow');
    try {
      const res = await fetch(`/api/posts/${encodeURIComponent(postId)}/follow`, {
        method: 'POST',
      });
      if (res.ok) setFollowing((f) => !f);
    } finally {
      setBusy(null);
    }
  }, [postId, busy]);

  return (
    <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border bg-background/60">
      <button
        type="button"
        onClick={close}
        className="h-9 px-4 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
      >
        Close
      </button>
      <button
        type="button"
        onClick={openThread}
        className="h-9 px-4 rounded-lg text-sm font-medium border border-border text-foreground hover:bg-accent transition-colors inline-flex items-center gap-1.5"
      >
        <MessageSquare className="h-3.5 w-3.5" />
        {t('workspace.preview.viewThread')}
      </button>
      <button
        type="button"
        onClick={toggleSave}
        disabled={busy === 'save'}
        className="h-9 px-4 rounded-lg text-sm font-medium border border-border text-foreground hover:bg-accent transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
      >
        {busy === 'save' ? (
          <Spinner size="xs" tone="muted" />
        ) : (
          <Bookmark className={`h-3.5 w-3.5 ${saved ? 'fill-current' : ''}`} />
        )}
        {saved ? t('discover.scopeSaved') : t('actions.save')}
      </button>
      <button
        type="button"
        onClick={toggleFollow}
        disabled={busy === 'follow'}
        data-testid="preview-follow-post"
        className="h-9 px-4 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
      >
        {busy === 'follow' ? (
          <Spinner size="xs" tone="onDark" />
        ) : (
          <Bell className="h-3.5 w-3.5" />
        )}
        {following ? t('workspace.preview.following') : t('workspace.preview.follow')}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Content preview (/preview/content?contentId=xxx)
// ---------------------------------------------------------------------------

type ContentSummary = {
  id: string;
  displayName: string;
  mimeType: string;
  contentType: string;
  size: number;
  metadata?: Record<string, unknown> | null;
  creator?: {
    username: string;
    displayName?: string | null;
    avatarUrl?: string | null;
  };
};

export function ContentPreview({ params }: { params: URLSearchParams }) {
  const t = useT();
  const contentId = params.get('contentId') ?? '';
  const [info, setInfo] = useState<ContentSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!contentId) {
      setError('No content id');
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/content/${encodeURIComponent(contentId)}/info`);
        if (!res.ok) {
          if (!cancelled) {
            setError(res.status === 404 ? t('workspace.preview.contentNotFound') : t('workspace.preview.contentLoadFailed'));
          }
          return;
        }
        const data = (await res.json()) as ContentSummary;
        if (!cancelled) setInfo(data);
      } catch {
        if (!cancelled) setError(t('workspace.preview.contentLoadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contentId]);

  if (loading) return <PreviewSpinner />;
  if (error || !info) return <PreviewError message={error ?? t('workspace.preview.contentNotFound')} />;

  const isImage = info.contentType === 'image';
  const isVideo = info.contentType === 'video';
  const sizeLabel = formatBytes(info.size);

  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center justify-center bg-muted/40 rounded-lg overflow-hidden">
        {isImage ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={`/api/content/${encodeURIComponent(info.id)}`}
            alt={info.displayName}
            className="max-h-72 w-full object-contain"
          />
        ) : isVideo ? (
          <video
            src={`/api/content/${encodeURIComponent(info.id)}`}
            controls
            className="max-h-72 w-full"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
            {info.contentType === 'image' ? (
              <ImageIcon className="h-10 w-10 opacity-60" />
            ) : (
              <FileText className="h-10 w-10 opacity-60" />
            )}
            <span className="text-xs">{info.mimeType}</span>
          </div>
        )}
      </div>
      <div className="space-y-1 text-center">
        <h2 className="text-base font-semibold">{info.displayName}</h2>
        <p className="text-xs text-muted-foreground">
          {info.contentType}
          {sizeLabel && <span> &middot; {sizeLabel}</span>}
          {info.creator && <span> &middot; @{info.creator.username}</span>}
        </p>
      </div>
    </div>
  );
}

export function ContentPreviewFooter({ params }: { params: URLSearchParams }) {
  const contentId = params.get('contentId') ?? '';
  const { addItem } = useWorkspaceActions();
  const { close } = useFullscreenNav();
  const [working, setWorking] = useState(false);

  const onAdd = useCallback(async () => {
    if (working || !contentId) return;
    setWorking(true);
    try {
      const res = await fetch(`/api/content/${encodeURIComponent(contentId)}/info`);
      let cache: Record<string, unknown> = {};
      if (res.ok) {
        const info = (await res.json()) as ContentSummary;
        cache = {
          displayName: info.displayName,
          contentType: info.contentType,
        };
      }
      await addItem({ type: 'content', refId: contentId, state: { cache } });
      close();
    } finally {
      setWorking(false);
    }
  }, [contentId, addItem, close, working]);

  return <AddToWorkspaceFooter onAdd={onAdd} working={working} />;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function AddToWorkspaceFooter({ onAdd, working }: { onAdd: () => void; working: boolean }) {
  const { close } = useFullscreenNav();
  return (
    <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border bg-background/60">
      <button
        type="button"
        onClick={close}
        className="h-9 px-4 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
      >
        Close
      </button>
      <button
        type="button"
        onClick={onAdd}
        disabled={working}
        data-testid="preview-add-to-workspace"
        className="h-9 px-4 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
      >
        {working ? (
          <Spinner size="xs" tone="onDark" />
        ) : (
          <Plus className="h-3.5 w-3.5" />
        )}
        Add to workspace
      </button>
    </div>
  );
}

function PreviewSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <Spinner size="lg" tone="muted" />
    </div>
  );
}

function PreviewError({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <AlertCircle className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function formatBytes(n: number): string {
  if (!n || n < 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  const rounded = value < 10 ? value.toFixed(1) : Math.round(value).toString();
  return `${rounded} ${units[unit]}`;
}
